// A supplier may quote the SAME inquiry item with several SKUs — backend
// uniqueness is per (item, product), not per (item, supplier). The match-maker
// matrix therefore cannot assume one quote per supplier per item: it renders
// one column per quote "slot", where a supplier's slot count is the most
// quotes they placed on any single item in the category. These helpers keep
// that logic pure and testable.

// Structural subset of RestaurantInquiryQuote — enough for slot math.
interface SlotQuote {
  id: number;
  supplierId: number;
  status: string;
  price: number;
}

// All of one supplier's quotes on an item: live (non-rejected) quotes first so
// the leftmost slot is always the active offer — a rejected quote must never
// mask a live one — then by id for a stable layout across renders.
export function orderedSupplierQuotes<T extends SlotQuote>(quotes: T[] | undefined, sid: number): T[] {
  return (quotes || [])
    .filter((q) => q.supplierId === sid)
    .sort((a, b) => {
      const aRejected = a.status === 'REJECTED' ? 1 : 0;
      const bRejected = b.status === 'REJECTED' ? 1 : 0;
      return aRejected - bRejected || a.id - b.id;
    });
}

// Columns a supplier needs: their max quotes on any single item (min 1 so a
// quoting supplier always keeps a summary column).
export function supplierSlotCount(items: Array<{ quotes?: SlotQuote[] }>, sid: number): number {
  return Math.max(1, ...items.map((it) => (it.quotes || []).filter((q) => q.supplierId === sid).length));
}

// The quote Award should accept when a supplier has several PENDING quotes on
// one item: the cheapest (ties → oldest). Accepting cascades server-side and
// rejects the sibling quotes, so exactly one must be chosen.
export function bestPendingQuote<T extends SlotQuote>(quotes: T[] | undefined, sid: number): T | undefined {
  return (quotes || [])
    .filter((q) => q.supplierId === sid && q.status === 'PENDING')
    .sort((a, b) => a.price - b.price || a.id - b.id)[0];
}

// A quote carries no price yet. Auto-quotation stores 0 for a supplier that
// matches the item but has no row in supplier_prices — 0 is a placeholder for
// "not priced", never a real offer, so it must not be shown as money, win a
// cheapest comparison, or bound a range.
export function isPriced(price: number | null | undefined): boolean {
  return price != null && price > 0;
}

// One live offer per entry, with the unit its price RESOLVED to (the display
// unit, not the stored PACK/ITEM/BASE rung — two PACK quotes on different SKUs
// can resolve to different physical units, and a range across those would lie).
export interface OfferSummaryInput {
  price: number;
  currency: string;
  unit?: string;
}

export type CellPriceSummary =
  | { kind: 'single'; price: number; currency: string; unit?: string }
  | { kind: 'range'; min: number; max: number; currency: string; unit?: string }
  // Units differ → only "from <cheapest raw price>" is honest. Raw-price
  // cheapest matches the face's long-standing pick of `shown`.
  | { kind: 'from'; min: number; currency: string };

export function cellPriceSummary(offers: OfferSummaryInput[]): CellPriceSummary | null {
  // Unpriced offers are dropped before any arithmetic: a 0 would otherwise be
  // the cheapest number in every cell and drag every range down to it.
  const priced = offers.filter((offer) => isPriced(offer.price));
  if (!priced.length) return null;
  const [first] = priced;
  if (priced.length === 1) return { kind: 'single', price: first.price, currency: first.currency, unit: first.unit };
  const min = Math.min(...priced.map((o) => o.price));
  const max = Math.max(...priced.map((o) => o.price));
  // All-undefined units count as "same": those quotes never had a unit suffix
  // on the face, so their raw prices were always compared as-is.
  if (!priced.every((o) => o.unit === first.unit)) return { kind: 'from', min, currency: first.currency };
  if (min === max) return { kind: 'single', price: min, currency: first.currency, unit: first.unit };
  return { kind: 'range', min, max, currency: first.currency, unit: first.unit };
}
